/**
 * Deterministic, seedable pseudo-random number generator (mulberry32).
 *
 * Every stochastic part of an experiment (pipe layout, simulated physiological
 * latency, simulated response failures) draws from an explicitly seeded RNG so
 * that a session can be reproduced exactly from its seed + event log.
 *
 * Never use Math.random() anywhere in this codebase.
 */
export class Rng {
  private state: number;

  constructor(public readonly seed: number) {
    this.state = seed >>> 0;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Uniform integer in [min, max]. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** Standard normal deviate (Box-Muller, second value discarded). */
  normal(mean = 0, stdDev = 1): number {
    const u1 = Math.max(this.next(), Number.EPSILON);
    const u2 = this.next();
    return mean + stdDev * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  }

  /** True with probability p. */
  bernoulli(p: number): boolean {
    return this.next() < p;
  }

  /** Snapshot / restore so a replay can fork the stream. */
  getState(): number {
    return this.state;
  }

  setState(state: number): void {
    this.state = state >>> 0;
  }

  clone(): Rng {
    const r = new Rng(this.seed);
    r.setState(this.state);
    return r;
  }
}

/** Turn an arbitrary string into a 32-bit seed (for human-readable seeds). */
export function hashSeed(text: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}
